import React from "react";
import MetricInfoTooltip from "components/MetricInfoTooltip";

export interface RadarAxis {
  key: "speed" | "reliability" | "stability" | "value" | "circulation";
  label: string;
  value: number;
}

interface CompanyRadarChartProps {
  axes: RadarAxis[];
  isLoading: boolean;
  isError: boolean;
}

const LABEL_GAP = 20;

const getLabelPosition = (
  angle: number,
  radius: number,
  centerX: number,
  centerY: number,
) => {
  const directionX = Math.cos(angle);
  const directionY = Math.sin(angle);
  const axisX = centerX + directionX * radius;
  const axisY = centerY + directionY * radius;
  const isVertical = Math.abs(directionX) < 0.25;

  return {
    x: axisX + directionX * LABEL_GAP,
    y: axisY + directionY * LABEL_GAP,
    textAnchor: isVertical ? "middle" : directionX > 0 ? "start" : "end",
    dominantBaseline:
      directionY < -0.9 ? "auto" : directionY > 0.9 ? "hanging" : "middle",
  } as const;
};

const getLabelTransformClass = (
  textAnchor: "middle" | "start" | "end",
  dominantBaseline: "auto" | "hanging" | "middle",
) => {
  if (textAnchor === "middle" && dominantBaseline === "auto") {
    return "-translate-x-1/2 -translate-y-full";
  }

  if (textAnchor === "middle" && dominantBaseline === "hanging") {
    return "-translate-x-1/2";
  }

  if (textAnchor === "end") {
    return "-translate-x-full -translate-y-1/2";
  }

  return "-translate-y-1/2";
};

const polygonPoints = (
  values: number[],
  radius: number,
  centerX: number,
  centerY: number,
) =>
  values
    .map((value, index) => {
      const angle = -Math.PI / 2 + (index * Math.PI * 2) / values.length;
      const scaledRadius = radius * (value / 100);
      return `${centerX + Math.cos(angle) * scaledRadius},${
        centerY + Math.sin(angle) * scaledRadius
      }`;
    })
    .join(" ");

const regularPolygonPoints = (
  count: number,
  radius: number,
  centerX: number,
  centerY: number,
) =>
  Array.from({ length: count }, (_, index) => {
    const angle = -Math.PI / 2 + (index * Math.PI * 2) / count;
    return `${centerX + Math.cos(angle) * radius},${
      centerY + Math.sin(angle) * radius
    }`;
  }).join(" ");

const CompanyRadarChart: React.FC<CompanyRadarChartProps> = ({
  axes,
  isLoading,
  isError,
}) => {
  const centerX = 300;
  const centerY = 215;
  const radius = 160;

  return (
    <div className="relative mx-auto w-full max-w-[630px]">
      <svg
        viewBox="0 0 600 440"
        role="img"
        aria-label={`${axes.length}軸のレーダーチャート`}
        className="company-radar-chart block h-auto w-full"
      >
        <defs>
          <linearGradient
            id={`radar-fill-${axes.length}`}
            x1="0"
            y1="0"
            x2="1"
            y2="1"
          >
            <stop offset="0%" stopColor="#d4f754" stopOpacity="0.85" />
            <stop offset="58%" stopColor="#2cdcec" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#2cdcec" stopOpacity="0.45" />
          </linearGradient>
        </defs>

        {[0.25, 0.5, 0.75, 1].map((scale) => (
          <polygon
            key={scale}
            points={regularPolygonPoints(
              axes.length,
              radius * scale,
              centerX,
              centerY,
            )}
            fill="none"
            stroke="rgba(255,255,255,0.48)"
            strokeWidth="1"
          />
        ))}

        {axes.map((axis, index) => {
          const angle = -Math.PI / 2 + (index * Math.PI * 2) / axes.length;
          return (
            <line
              key={axis.key}
              x1={centerX}
              y1={centerY}
              x2={centerX + Math.cos(angle) * radius}
              y2={centerY + Math.sin(angle) * radius}
              stroke="rgba(255,255,255,0.35)"
              strokeWidth="1"
            />
          );
        })}

        {!isLoading && !isError && (
          <>
            <polygon
              points={polygonPoints(
                axes.map((axis) => axis.value),
                radius,
                centerX,
                centerY,
              )}
              fill={`url(#radar-fill-${axes.length})`}
              stroke="#d4f754"
              strokeLinejoin="round"
              strokeWidth="4"
            />
            {axes.map((axis, index) => {
              const angle = -Math.PI / 2 + (index * Math.PI * 2) / axes.length;
              const pointRadius = radius * (axis.value / 100);
              return (
                <circle
                  key={axis.key}
                  cx={centerX + Math.cos(angle) * pointRadius}
                  cy={centerY + Math.sin(angle) * pointRadius}
                  r="6"
                  fill="#d4f754"
                />
              );
            })}
          </>
        )}

        {(isLoading || isError) && (
          <text
            x={centerX}
            y={centerY}
            dominantBaseline="middle"
            textAnchor="middle"
            fill="white"
            fontFamily="Noto Sans JP, sans-serif"
            fontSize="16"
            fontWeight="700"
          >
            {isLoading ? "読み込み中..." : "データを取得できませんでした"}
          </text>
        )}
      </svg>
      {axes.map((axis, index) => {
        const angle = -Math.PI / 2 + (index * Math.PI * 2) / axes.length;
        const { x, y, textAnchor, dominantBaseline } = getLabelPosition(
          angle,
          radius,
          centerX,
          centerY,
        );

        return (
          <div
            key={axis.key}
            className={`absolute flex items-center gap-[5px] whitespace-nowrap font-['Noto_Sans_JP'] text-[13px] font-bold leading-[1.3] text-[#d4f754] sm:text-[14px] ${getLabelTransformClass(
              textAnchor,
              dominantBaseline,
            )}`}
            style={{
              left: `${(x / 600) * 100}%`,
              top: `${(y / 440) * 100}%`,
            }}
          >
            <span>{axis.label}</span>
            <MetricInfoTooltip
              metricKey={axis.key}
              label={axis.label}
              className="text-[#d4f754]"
            />
          </div>
        );
      })}
    </div>
  );
};

export default CompanyRadarChart;
